import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import type { ExportJobDto } from "@platform/core";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DataGrid,
  EmptyState,
  useToast,
  type GridColDef,
} from "@platform/ui";
import { useApi, useConsole } from "../../context";
import { useT } from "../../i18n";
import { downloadBlob } from "../../lib/download";
import { errorText } from "../../lib/errors";
import { formatDateTime } from "../../lib/format";
import { useGridText } from "../../lib/grid";
import { ErrorBanner, PageCard, ReloadButton, Section } from "../../layout/Page";

export function DataPage() {
  const { t, locale } = useT();
  const api = useApi();
  const { config } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const [includeAuditLogs, setLogs] = useState(false);
  const [includeCredentials, setCreds] = useState(false);
  const jobs = useQuery({
    queryKey: ["exports"],
    queryFn: api.exports.list,
    refetchInterval: (q) =>
      q.state.data?.items.some((j) => j.status === "pending" || j.status === "running") ? 2000 : false,
  });
  const start = useMutation({
    mutationFn: () => api.exports.start({ includeAuditLogs, includeCredentials }),
    onSuccess: () => {
      toast({ title: t("data.started"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["exports"] });
    },
  });
  const download = useMutation({
    mutationFn: async (id: string) => downloadBlob(await api.exports.download(id), `auth-export-${id}.jsonl`),
  });
  const tone = { pending: "neutral", running: "info", done: "success", failed: "danger" } as const;
  const text = useGridText();
  const columns: GridColDef<ExportJobDto>[] = [
    {
      field: "createdAt",
      headerName: t("logs.col.time"),
      type: "dateTime",
      width: 190,
      valueFormatter: (v) => formatDateTime(String(v), locale, config.timeZone),
    },
    {
      field: "status",
      headerName: t("users.col.status"),
      type: "singleSelect",
      width: 200,
      valueOptions: (["pending", "running", "done", "failed"] as const).map((s) => ({
        value: s,
        label: t(`data.status.${s}`),
      })),
      renderCell: ({ row: j, formatted }) => (
        <div>
          <Badge dot tone={tone[j.status]}>
            {formatted}
          </Badge>
          {j.error && <p className="mt-1 truncate text-xs text-danger">{j.error}</p>}
        </div>
      ),
    },
    { field: "scope", headerName: "Scope", minWidth: 160, cellClassName: "font-mono text-xs" },
    {
      field: "finishedAt",
      headerName: t("data.finished"),
      type: "dateTime",
      width: 190,
      hiddenByDefault: true,
      valueFormatter: (v) => (v ? formatDateTime(String(v), locale, config.timeZone) : ""),
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      width: 150,
      align: "right",
      hideable: false,
      disableExport: true,
      renderCell: ({ row: j }) =>
        j.status === "done" ? (
          <Button variant="text" size="sm" onClick={() => download.mutate(j.id)}>
            <Download className="size-4" aria-hidden />
            {t("common.download")}
          </Button>
        ) : null,
    },
  ];
  return (
    <PageCard>
      <Section title={t("data.start")}>
        <div className="mb-4 space-y-2">
          <Checkbox
            label={t("data.includeLogs")}
            checked={includeAuditLogs}
            onChange={(e) => setLogs(e.target.checked)}
          />
          <Checkbox
            label={t("data.includeCredentials")}
            checked={includeCredentials}
            onChange={(e) => setCreds(e.target.checked)}
          />
        </div>
        {start.isError && (
          <Alert tone="error" className="mb-3">
            {errorText(start.error)}
          </Alert>
        )}
        <Button loading={start.isPending} onClick={() => start.mutate()}>
          {t("data.start")}
        </Button>
      </Section>
      <section className="border-b border-line">
        <h2 className="px-6 pt-5 pb-1 text-base font-semibold">{t("data.jobs")}</h2>
        {jobs.isError && <ErrorBanner error={jobs.error} onRetry={() => void jobs.refetch()} />}
        <DataGrid
          id="export-jobs"
          label={t("data.jobs")}
          localeText={text}
          rows={jobs.data?.items ?? []}
          columns={columns}
          loading={jobs.isFetching}
          defaultSortModel={[{ field: "createdAt", sort: "desc" }]}
          noRowsContent={<EmptyState title={t("data.empty")} />}
          toolbarActions={<ReloadButton onClick={() => void jobs.refetch()} />}
        />
      </section>
      <Section title={t("data.import")}>
        <p className="font-mono text-xs text-ink-2">{t("data.importHint")}</p>
      </Section>
    </PageCard>
  );
}
